import { useEffect, useState } from "react";
import { Loader2, Lock } from "lucide-react";
import { siteAccessStatus, siteAccessUnlock } from "@/lib/api";

/**
 * Whole-site password gate — maintenance mode (Feb 2026).
 *
 * On mount: GET /api/site-access/status. If the gate is enabled and the
 * visitor isn't unlocked, render a full-page maintenance screen with a
 * discreet password unlock. Otherwise render `children` (the normal app).
 * Once unlocked the backend sets a 30-day cookie and we re-show the app.
 *
 * Carve-outs (paths that stay live during maintenance):
 *   - /classroom-support/*  — public top-of-funnel (SEO + free tool)
 *   - /privacy, /terms       — legal pages
 */

// Paths whose subtree stays public even when the site lock is on.
// Jul 2026 SEO URLs lane — /free-lesson-generator, /lesson-from-resource,
// and its /try soft-redirect join the carve-outs so search visitors
// landing from Google reach the tool, not the maintenance wall. The
// tool-level sign-in on /lesson-from-resource is its own gate; the
// page itself must be reachable.
const PUBLIC_PATH_PREFIXES = ["/classroom-support", "/privacy", "/terms", "/featured-lesson", "/supply-plans", "/free-lesson-generator", "/lesson-from-resource", "/try", "/links", "/samples", "/ontario-report-cards"];

const isPublicPath = (pathname) =>
  PUBLIC_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(prefix + "/"),
  );

const SiteGate = ({ children }) => {
  const [state, setState] = useState("checking"); // checking | open | locked
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showUnlock, setShowUnlock] = useState(false);

  const pathname = typeof window !== "undefined" ? window.location.pathname : "";
  const publicPath = isPublicPath(pathname);

  useEffect(() => {
    if (publicPath) {
      setState("open");
      return;
    }
    let cancelled = false;
    siteAccessStatus()
      .then((s) => {
        if (cancelled) return;
        if (!s.gate_enabled || s.unlocked) setState("open");
        else setState("locked");
      })
      .catch(() => {
        if (cancelled) return;
        // If we can't even reach /status, fail open — don't hard-lock the
        // user out of the app over a transient network blip.
        setState("open");
      });
    return () => {
      cancelled = true;
    };
  }, [publicPath]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting || !password.trim()) return;
    setSubmitting(true);
    setError("");
    try {
      await siteAccessUnlock(password.trim());
      // Reload so every cached fetch + the AuthProvider re-runs cleanly
      // against the now-unlocked backend.
      window.location.reload();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Incorrect password";
      setError(msg);
      setSubmitting(false);
    }
  };

  if (state === "checking") {
    return (
      <div className="min-h-screen grid place-items-center bg-[#f4f5f7]">
        <Loader2 className="h-6 w-6 animate-spin text-[#4a6984]" />
      </div>
    );
  }

  if (state === "open") return children;

  return (
    <div
      data-testid="site-gate"
      className="min-h-screen flex flex-col items-center justify-center bg-[#f4f5f7] text-[#1c2d3d] px-6 py-12"
      style={{ fontFamily: "Montserrat, sans-serif" }}
    >
      <div className="max-w-xl w-full text-center">
        {/* Brand mark */}
        <div className="flex items-center justify-center gap-3 mb-10">
          <div className="h-11 w-11 rounded-lg bg-[#4a6984] grid place-items-center font-extrabold text-white text-lg">
            A
          </div>
          <span className="font-extrabold text-2xl tracking-tight text-[#4a6984]">
            Atomic Edu
          </span>
        </div>

        {/* Mint accent chip */}
        <div className="inline-flex items-center gap-2 rounded-full bg-[#96ded1]/25 border border-[#96ded1] px-4 py-1.5 mb-6">
          <span className="h-2 w-2 rounded-full bg-[#4a6984] animate-pulse" />
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#4a6984]">
            Maintenance
          </p>
        </div>

        <h1
          data-testid="site-gate-heading"
          className="font-extrabold text-3xl sm:text-4xl tracking-tight leading-tight text-[#1c2d3d]"
        >
          We&apos;re behind the scenes making things better.
        </h1>

        <p className="mt-6 text-base sm:text-lg leading-relaxed text-[#4a6984]">
          Nothing you need to do. We&apos;ll be back with a stronger version of the
          site in a couple of weeks.
        </p>

        <p className="mt-5 text-sm sm:text-base leading-relaxed text-[#4a6984]">
          If you need to reach us in the meantime, email is at:
        </p>
        <p className="mt-2">
          <a
            href="mailto:info@atomicedu.ca"
            data-testid="site-gate-email"
            className="text-base sm:text-lg font-bold text-[#4a6984] underline decoration-[#96ded1] decoration-2 underline-offset-4 hover:text-[#1c2d3d] transition-colors"
          >
            info@atomicedu.ca
          </a>
        </p>

        {/* Discreet team unlock */}
        <div className="mt-14">
          {!showUnlock ? (
            <button
              type="button"
              data-testid="site-gate-toggle"
              onClick={() => setShowUnlock(true)}
              className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-[#4a6984]/60 hover:text-[#4a6984] transition-colors"
            >
              <Lock className="h-3 w-3" />
              Team access
            </button>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="mx-auto max-w-sm text-left"
            >
              <label
                htmlFor="site-gate-password"
                className="block text-[11px] font-bold uppercase tracking-[0.18em] text-[#4a6984]/70 mb-2 text-center"
              >
                Team password
              </label>
              <div className="flex gap-2">
                <input
                  id="site-gate-password"
                  data-testid="site-gate-password"
                  type="password"
                  autoFocus
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={submitting}
                  placeholder="••••••••"
                  className="flex-1 h-11 rounded-md bg-white border border-[#4a6984]/25 px-4 text-[#1c2d3d] placeholder-[#4a6984]/40 focus:outline-none focus:ring-2 focus:ring-[#96ded1] focus:border-[#96ded1] font-medium"
                />
                <button
                  type="submit"
                  data-testid="site-gate-submit"
                  disabled={submitting || !password.trim()}
                  className="h-11 px-5 rounded-md bg-[#4a6984] hover:bg-[#3a556e] text-white font-bold text-sm tracking-tight inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    "Enter"
                  )}
                </button>
              </div>
              {error ? (
                <p
                  data-testid="site-gate-error"
                  className="text-xs font-semibold text-[#c0392b] mt-3 text-center"
                >
                  {error}
                </p>
              ) : null}
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default SiteGate;
